/**
 * Clickable newest-first list of manual ingestion jobs.
 */

import type { JobStatusResponse } from "../../api/clinicalTrialsApiTypes";
import { formatJobSource, formatJobTimestamp } from "./jobFormatters";
import { JobStatusBadge } from "./JobStatusBadge";

interface JobHistoryListProps {
  jobs: JobStatusResponse[];
  selectedRunId?: string;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  totalCount: number;
  onSelect: (runId: string) => void;
  onRetry: () => void;
  onLoadMore: () => void;
}

/**
 * Render manual jobs with status, source, start time, and progress.
 *
 * @param props - History state and interaction callbacks
 * @returns Clickable manual job history
 */
export function JobHistoryList({
  jobs,
  selectedRunId,
  loading,
  loadingMore,
  error,
  hasMore,
  totalCount,
  onSelect,
  onRetry,
  onLoadMore,
}: JobHistoryListProps) {
  return (
    <section className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
        <div>
          <h2 className="text-lg font-semibold">Past ingestion jobs</h2>
          <p className="text-xs text-gray-500">
            {totalCount} manual run{totalCount === 1 ? "" : "s"}
          </p>
        </div>
        <button
          onClick={onRetry}
          disabled={loading}
          className="rounded-md border px-3 py-1.5 text-sm font-medium"
          style={{
            borderColor: "var(--color-primary)",
            color: "var(--color-primary)",
          }}
        >
          Refresh
        </button>
      </div>

      {error && (
        <div className="border-b border-red-100 bg-red-50 px-5 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {loading && !jobs.length ? (
        <div className="p-8 text-center text-sm text-gray-500">
          Loading ingestion history…
        </div>
      ) : !jobs.length ? (
        <div className="p-8 text-center text-sm text-gray-500">
          No manual ingestion jobs have run yet.
        </div>
      ) : (
        <div className="divide-y divide-gray-100">
          {jobs.map((job) => {
            const selected = selectedRunId === job.run_id;
            return (
              <button
                key={job.run_id}
                onClick={() => onSelect(job.run_id)}
                aria-pressed={selected}
                className="grid w-full grid-cols-[1fr_auto] gap-3 px-5 py-4 text-left transition-colors hover:bg-slate-50"
                style={{
                  backgroundColor: selected ? "#f0f9ff" : undefined,
                  borderLeft: selected
                    ? "3px solid var(--color-primary)"
                    : "3px solid transparent",
                }}
              >
                <span className="min-w-0">
                  <span className="mb-1 flex items-center gap-2">
                    <JobStatusBadge status={job.status} />
                    <span className="text-sm font-medium">
                      {formatJobSource(job.source)}
                    </span>
                  </span>
                  <span className="block text-xs text-gray-500">
                    Started {formatJobTimestamp(job.started_at)}
                  </span>
                  <span className="block truncate font-mono text-xs text-gray-400">
                    {job.run_id}
                  </span>
                </span>
                <span className="self-center text-right text-sm">
                  <span className="block font-semibold">
                    {job.processed_count} / {job.total || "—"}
                  </span>
                  <span className="text-xs text-gray-500">
                    {job.batch_count
                      ? `${job.completed_batch_count}/${job.batch_count} batches`
                      : job.failed_count
                        ? `${job.failed_count} failed`
                        : "patients"}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      {hasMore && (
        <div className="border-t border-gray-200 p-3 text-center">
          <button
            onClick={onLoadMore}
            disabled={loadingMore}
            className="rounded-md px-4 py-2 text-sm font-medium"
            style={{ color: "var(--color-primary)" }}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      )}
    </section>
  );
}
